import {
  EnvConfig,
  MediaType,
  useCurrentWorkspace,
  useCustomQuery,
} from '@gripp/shared-logic';
import { Colors, ActivityCard, tokens, TypeIcon } from '@gripp/shared-ui';
import { useTranslation } from 'react-i18next';
import {
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useFindMediaByIds, useGetCampaign } from '../hooks/useCampaigns';

const FIND_CAMPAIGN_ISSUES = `
  query FindCampaignIssues($filter: IssueFilter, $page: PageRequest, $orderBy: OrderByInput) {
    findIssues(filter: $filter, page: $page, orderBy: $orderBy) {
      items {
        id
        title
        updatedAt
        asset {
          id
          name
        }
      }
    }
  }
`;

const FIND_LATEST_MESSAGES_FOR_ISSUES = `
  query FindIssueMessagesForCampaign(
    $filter: IssueMessageFilter
    $page: PageRequest
    $orderBy: OrderByInput
  ) {
    findIssueMessages(filter: $filter, page: $page, orderBy: $orderBy) {
      items {
        id
        type
        text
        createdAt
        issue {
          id
        }
      }
    }
  }
`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * A single response row, corresponding to one targeted asset in a campaign.
 */
export interface CampaignResponseRow {
  /** The asset id this response is for. */
  assetId: string;
  /** The issue id for this asset's campaign thread (if known). */
  issueId?: string;
  /** Display label for the asset. */
  assetLabel: string;
  /** Campaign thread title (shown next to the pink campaign type icon). */
  threadTitle: string;
  /** Latest message snippet from the thread. */
  latestMessageSnippet?: string;
  /** Date string to display as a day chip. */
  dayLabel?: string;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CampaignResponsesPanelProps {
  /** The campaign whose responses to display. */
  campaignId: string;
  /** Fired when the user selects a response row — opens the Thread panel. */
  onResponseSelect: (row: CampaignResponseRow) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Format a date string into a short day label (e.g. "May 20").
 */
const formatDayLabel = (dateStr?: string): string | undefined => {
  if (!dateStr) return undefined;
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return undefined;
  }
};

interface FoundIssue {
  id: string;
  title: string;
  updatedAt?: string;
  asset?: { id: string; name?: string };
}

interface FoundIssueMessage {
  id: string;
  type: string;
  text?: string;
  createdAt?: string;
  issue?: { id: string };
}

/**
 * Build a Map<issueId, latestReply> by grouping messages per issue and
 * dropping the chronologically-earliest message per issue — that one is the
 * dispatch seed (every CAMPAIGN issue's first IssueMessage is the seed posted
 * by dispatchCampaign, attributed to the campaign creator). Issues with no
 * replies beyond the seed do not appear in the map.
 */
const indexLatestRepliesByIssue = (
  messages: FoundIssueMessage[]
): Map<string, FoundIssueMessage> => {
  const byIssue = new Map<string, FoundIssueMessage[]>();
  messages.forEach((m) => {
    const issueId = m.issue?.id;
    if (!issueId) return;
    const arr = byIssue.get(issueId) ?? [];
    arr.push(m);
    byIssue.set(issueId, arr);
  });

  const latest = new Map<string, FoundIssueMessage>();
  byIssue.forEach((msgs, issueId) => {
    if (msgs.length < 2) return; // seed only — no replies yet
    const sorted = [...msgs].sort(
      (a, b) =>
        new Date(a.createdAt ?? 0).getTime() -
        new Date(b.createdAt ?? 0).getTime()
    );
    const lastReply = sorted[sorted.length - 1];
    latest.set(issueId, lastReply);
  });
  return latest;
};

const deriveRowsFromIssues = (
  issues: FoundIssue[],
  campaignTitle: string,
  latestByIssue: Map<string, FoundIssueMessage>
): CampaignResponseRow[] =>
  issues
    // Only Issues with at least one reply beyond the dispatch seed. Targeted
    // assets that haven't replied yet do not surface here — the Responses
    // panel is for actual responses, not a per-asset roster.
    .filter((issue) => latestByIssue.has(issue.id))
    .map((issue) => {
      const asset = issue.asset;
      const latest = latestByIssue.get(issue.id);
      return {
        assetId: asset?.id ?? issue.id,
        issueId: issue.id,
        assetLabel: asset?.name ?? asset?.id ?? '—',
        threadTitle: issue.title || campaignTitle,
        latestMessageSnippet: latest?.text,
        dayLabel: formatDayLabel(latest?.createdAt ?? issue.updatedAt),
      };
    });

// ---------------------------------------------------------------------------
// Row component
// ---------------------------------------------------------------------------

interface ResponseRowProps {
  row: CampaignResponseRow;
  onPress: (row: CampaignResponseRow) => void;
}

// Renders the same Crop Circle ActivityCard used across the activity feed so a
// campaign response reads like any other asset activity: QR-icon asset-name
// pill, pink 'campaign' type icon + (black) title, and a greyed reply snippet.
// showImage is off — the responses query doesn't hydrate asset cover images and
// the QR pill already carries the asset identity.
const ResponseRow = ({ row, onPress }: ResponseRowProps) => (
  <Pressable
    onPress={() => onPress(row)}
    style={styles.row}
    accessibilityRole="button"
    accessibilityLabel={`Open campaign thread for ${row.assetLabel}`}
    testID={`campaign-response-row-${row.assetId}`}
  >
    <ActivityCard
      assetName={row.assetLabel}
      showImage={false}
      title={row.threadTitle}
      date={row.dayLabel ?? ''}
      description={row.latestMessageSnippet}
      severityVariant="campaign"
    />
  </Pressable>
);

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

/**
 * CampaignResponsesPanel
 *
 * Renders one row per targeted asset for the selected campaign. Each row
 * shows: asset label, thread title (in campaign pink), latest-message snippet,
 * and a day chip. Clicking a row fires `onResponseSelect` so the parent page
 * can open the Thread panel.
 *
 * Data source: campaign.targetedAssetIds from getCampaign. A richer
 * `listCampaignResponses` endpoint (with per-issue snippet + unread count)
 * is deferred to Phase 4.
 */
export const CampaignResponsesPanel = ({
  campaignId,
  onResponseSelect,
}: CampaignResponsesPanelProps) => {
  const { t } = useTranslation();
  const workspace = useCurrentWorkspace();

  const { data: campaignData, isLoading: isCampaignLoading } = useGetCampaign(
    workspace?.id ? campaignId : ''
  );

  const campaign = (
    campaignData as
      | {
          campaign?: {
            getCampaign?: {
              title: string;
              description?: string;
              mediaIds?: string[];
              sentAt?: string;
              scheduledSendAt?: string;
              createdAt?: string;
            };
          };
        }
      | undefined
  )?.campaign?.getCampaign;

  // Hydrate the first media item so the header card can show a thumbnail.
  // We only need the first id — fetching just one keeps the round-trip small.
  const firstMediaId = campaign?.mediaIds?.[0];
  const { data: mediaData } = useFindMediaByIds(
    firstMediaId ? [firstMediaId] : []
  );
  const firstMedia = (
    mediaData as
      | {
          findMedia?: {
            items?: Array<{
              id: string;
              type?: MediaType;
              extension?: string;
              url?: string;
            }>;
          };
        }
      | undefined
  )?.findMedia?.items?.[0];

  const thumbnailUri =
    firstMedia && firstMedia.type === MediaType.Image
      ? firstMedia.url ??
        (firstMedia.id && firstMedia.extension
          ? `${EnvConfig.mediaUrl}/images/${firstMedia.id}/original.${firstMedia.extension}`
          : undefined)
      : undefined;

  // Fetch the Issues created by this campaign (one per targeted asset). Each
  // Issue's id is the thread we'll navigate to on row click.
  const { data: issuesData, isLoading: areIssuesLoading } = useCustomQuery({
    query: FIND_CAMPAIGN_ISSUES,
    variables: {
      filter: { campaign: { eq: campaignId } },
      page: { limit: 200, offset: 0 },
      orderBy: { field: 'updatedAt', order: 'DESC' },
    },
    cacheKey: ['campaignIssues', campaignId],
    options: { enabled: !!workspace?.id && !!campaignId },
  });

  const issues = (
    issuesData as { findIssues?: { items?: FoundIssue[] } } | undefined
  )?.findIssues?.items ?? [];

  const issueIds = issues.map((i) => i.id);

  const { data: messagesData, isLoading: areMessagesLoading } = useCustomQuery({
    query: FIND_LATEST_MESSAGES_FOR_ISSUES,
    variables: {
      filter: issueIds.length
        ? { issue: { in: issueIds } }
        : { issue: { in: ['__none__'] } },
      page: { limit: 500, offset: 0 },
      orderBy: { field: 'createdAt', order: 'DESC' },
    },
    // Start the key with 'issuemessage' so useCreateIssueMessage's invalidation
    // (which targets cacheKeys('issueMessage').all === ['issuemessage']) also
    // refreshes this query when a reply is posted.
    cacheKey: ['issuemessage', 'campaign', campaignId, ...issueIds],
    options: { enabled: issueIds.length > 0, staleTime: 0 },
  });

  const messages = (
    messagesData as
      | { findIssueMessages?: { items?: FoundIssueMessage[] } }
      | undefined
  )?.findIssueMessages?.items ?? [];

  const latestByIssue = indexLatestRepliesByIssue(messages);

  const isLoading = isCampaignLoading || areIssuesLoading || areMessagesLoading;

  const handleRowPress = (row: CampaignResponseRow) => {
    onResponseSelect(row);
  };

  if (isLoading) {
    return (
      <View style={styles.centeredMessage}>
        <Text style={styles.centeredMessageText}>
          {t('campaigns.responses.loading', 'Loading responses…')}
        </Text>
      </View>
    );
  }

  if (!campaign) {
    return (
      <View style={styles.centeredMessage}>
        <Text style={styles.centeredMessageText}>
          {t('campaigns.responses.notFound', 'Campaign not found')}
        </Text>
      </View>
    );
  }

  const rows = deriveRowsFromIssues(issues, campaign.title, latestByIssue);

  const headerDayLabel = formatDayLabel(
    campaign.sentAt ?? campaign.scheduledSendAt ?? campaign.createdAt
  );

  const headerCard = (
    <View style={styles.headerCard}>
      <View style={styles.headerThumb}>
        {thumbnailUri ? (
          <Image
            source={{ uri: thumbnailUri }}
            style={styles.headerThumbImage}
            resizeMode="cover"
          />
        ) : (
          <View style={styles.headerThumbFallback}>
            <TypeIcon variant="campaign" size={16} />
          </View>
        )}
      </View>
      <View style={styles.headerBody}>
        <View style={styles.headerTitleRow}>
          <TypeIcon variant="campaign" size={12} />
          <Text style={styles.headerTitle} numberOfLines={1}>
            {campaign.title}
          </Text>
        </View>
        {campaign.description ? (
          <Text style={styles.headerSnippet} numberOfLines={2}>
            {campaign.description}
          </Text>
        ) : null}
      </View>
      {headerDayLabel ? (
        <Text style={styles.headerDay}>{headerDayLabel}</Text>
      ) : null}
    </View>
  );

  if (rows.length === 0) {
    return (
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.contentContainer}
      >
        {headerCard}
        <View style={styles.emptyBlock}>
          <Text style={styles.centeredMessageText}>
            {t('campaigns.responses.empty', 'No responses yet')}
          </Text>
        </View>
      </ScrollView>
    );
  }

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
    >
      {headerCard}
      {rows.map((row) => (
        <ResponseRow
          key={row.issueId ?? row.assetId}
          row={row}
          onPress={handleRowPress}
        />
      ))}
    </ScrollView>
  );
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    paddingVertical: tokens.spacing.sm,
  },
  centeredMessage: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacing['2xl'],
  },
  centeredMessageText: {
    ...tokens.typography.body.small,
    color: Colors.role.textSecondary,
  },
  row: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: Colors.role.borderControl,
  },
  headerCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: tokens.spacing.md,
    backgroundColor: Colors.role.surfaceHover,
    paddingHorizontal: tokens.spacing.lg,
    paddingVertical: tokens.spacing.md,
    marginHorizontal: tokens.spacing.md,
    marginBottom: tokens.spacing.sm,
    borderRadius: tokens.borderRadius.lg,
  },
  headerThumb: {
    width: 64,
    height: 64,
    borderRadius: tokens.borderRadius.md,
    overflow: 'hidden',
    backgroundColor: Colors.role.surfaceActive,
  },
  headerThumbImage: {
    width: '100%',
    height: '100%',
  },
  headerThumbFallback: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerBody: {
    flex: 1,
    gap: tokens.spacing.xxs,
  },
  headerTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs,
  },
  headerTitle: {
    ...tokens.typography.body.smallEmphasised,
    color: Colors.role.textStrong,
    flexShrink: 1,
  },
  headerSnippet: {
    ...tokens.typography.label.medium,
    color: Colors.role.textSecondary,
  },
  headerDay: {
    ...tokens.typography.label.small,
    color: Colors.role.textSecondary,
  },
  emptyBlock: {
    padding: tokens.spacing['2xl'],
    alignItems: 'center',
  },
});
